import { ReactNode, useEffect, useState } from 'react';
import parse, { Element } from 'html-react-parser';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { gql } from '@apollo/client';
import IframeWrapper from '../../components/iframe-wrapper';
import Layout from '../../components/layout';
import Paywall from '../../components/paywall';
import Title from '../../components/title';
import { H1 } from '../../components/atoms/typography';
import {
  CMS_HOSTNAME,
  MEMBERSHIPS,
  PAID_MEMBERSHIP_LEVELS
} from '../../lib/constants';
import { client, getPostByURI } from '../../apollo-client';

import {
  getYoastHeadSEO,
  hasSufficientPrivileges,
  isEmbeddedVideo,
  isLinkToArticle,
  isMatchingLink,
  isUserAuthenticated
} from '../../lib/utils';
import { useAuth0User } from '@hooks/useAuth0User';
import { useAccessToken } from '@hooks/useAccessToken';
import { fetchPost } from '@api-service/content';

const JossysFPLPlanner = ({ post }) => {
  const [postContent, setPostContent] = useState('');
  const [hasAuthorisedContent, setHasAuthorisedContent] = useState(false);
  const { user } = useAuth0User();
  const { accessToken } = useAccessToken();
  const hasSufficientMembershipLevel = hasSufficientPrivileges(
    user,
    PAID_MEMBERSHIP_LEVELS[MEMBERSHIPS.STANDARD_MONTHLY_ID].LEVEL
  );

  useEffect(() => {
    if (!post) return;
    updatePostContent(post.content);
    if (hasSufficientMembershipLevel && !hasAuthorisedContent && accessToken) {
      loadAuthorisedContent();
    }
  }, [user, accessToken]);

  const updatePostContent = async (itemContent) => {
    let post_content = itemContent.toString();

    post_content = post_content.replace(
      /\r\n<h3>/g,
      '<p style="margin:0;padding:0;">&nbsp;</p><h3>'
    );
    const finalContent = post_content.replace(
      /\r\n<ul>/g,
      '<p style="margin:0;padding:0;">&nbsp;</p><ul>'
    );

    setPostContent(finalContent);
  };

  const loadAuthorisedContent = async () => {
    if (!accessToken) return;

    try {
      const rendered = await fetchPost('jossys-fpl-planner', accessToken);
      if (rendered) {
        updatePostContent(rendered);
      }
    } catch (error) {
      console.error('[content] failed to load authorised post', error);
      updatePostContent(`
      <div style="padding: 2rem; text-align: center; background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; margin: 2rem 0;">
        <h3 style="color: #856404; margin-bottom: 1rem;">Unable to Load Article</h3>
        <p style="color: #856404; margin-bottom: 1.5rem;">We're having trouble loading this content. Please check your connection and try again.</p>
      </div>
    `);
    }

    setHasAuthorisedContent(true);
  };

  const router = useRouter();
  const pathname = router.pathname;
  const yoastHead = getYoastHeadSEO(post?.seo?.fullHead, pathname);

  return (
    <>
      <Head>{yoastHead}</Head>

      <Title>{parse(post.title) as ReactNode}</Title>

      <Layout>
        <div className="article">
          <div className="relative px-4 pt-8 pb-20 bg-white sm:px-6 lg:pb-10 lg:px-8">
            <div className="relative mx-auto max-w-7xl pb-14">
              <article className="article planner">
                <div className="mb-8 text-center">
                  <H1 className="text-center">{parse(post.title)}</H1>
                </div>

                <figure className="mb-6">
                  <img
                    alt=""
                    className="w-full rounded-lg"
                    src={post.featuredImage?.node?.sourceUrl}
                  />
                </figure>

                <div className="mt-3 text-base text-gray-500 content-body">
                  {parse(postContent, {
                    replace: (domNode: Element) => {
                      if (
                        domNode.attribs &&
                        domNode.attribs.class &&
                        domNode.attribs.class === 'epyt-video-wrapper'
                      ) {
                        // fixes double wrapping of iframe
                        domNode.attribs.class = '';

                        return { domNode };
                      }

                      if (isEmbeddedVideo(domNode)) {
                        return (
                          <IframeWrapper
                            id={domNode.attribs.id}
                            name={domNode.attribs.name}
                            src={domNode.attribs.src}
                            title={domNode.attribs.title}
                          />
                        );
                      }

                      if (isMatchingLink(domNode, '/jossy/49')) {
                        domNode.attribs.href = `${process.env.NEXT_PUBLIC_HOSTNAME}/join`;

                        return { domNode };
                      }

                      if (isLinkToArticle(domNode)) {
                        domNode.attribs.href = domNode.attribs.href.replace(
                          CMS_HOSTNAME,
                          `${process.env.NEXT_PUBLIC_HOSTNAME}`
                        );

                        return { domNode };
                      }
                    }
                  })}
                </div>
              </article>

              {!isUserAuthenticated(user) && <Paywall variant="embedded" />}
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export const getStaticProps = async () => {
  const { data } = await client.query({
    query: gql`
      ${getPostByURI('/jossys-fpl-planner')}
    `
  });

  const post = data.postBy;

  return {
    props: { post },
    revalidate: 600
  };
};

export default JossysFPLPlanner;
